import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CookieOptions, CookieService } from 'ngx-cookie';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { EventService } from '../shared/services/event.service';
import { LaunchAction, UrlLauncherService } from '../shared/services/url-launcher.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { LocService } from '../shared/shared-context/loc.service';
import { AnnouncementButtonModel } from './announcement-button.model';
import { AnnouncementModel } from './announcement.model';

@Component({
    selector: 'ohp-announcement',
    templateUrl: './announcement.component.html'
})
export class AnnouncementComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() public announcement: AnnouncementModel;

    public componentArea: string;
    public showAnnouncement = true;
    public dismissAriaLabel: string;
    public cssClassFont: string;
    public desktopBottomAligned: boolean;
    public announcementId: string;

    private readonly ngUnsubscribe = new Subject<void>();

    constructor(
        private readonly cookieService: CookieService,
        private readonly eventService: EventService,
        private readonly instrumentationService: InstrumentationService,
        private readonly locService: LocService,
        private readonly urlLauncherService: UrlLauncherService
    ) {
    }

    public ngOnInit() {
        this.cssClassFont = this.announcement.fontCssClass || '';
        this.componentArea = KnownAreas.Banner;
        this.dismissAriaLabel = this.locService.DismissDialogAriaLabel;
        this.announcementId = `announcement-${this.announcement.announcementType}-${this.announcement.index}`;

        // Check if announcement was already dismissed
        if (this.announcement.dismissCookieName) {
            const dismissed = this.cookieService.get(this.announcement.dismissCookieName);
            this.showAnnouncement = !dismissed;
        }

        if (this.showAnnouncement) {
            const params: InstrumentationParams = {
                [InstrumentationParam.ElementId]: this.announcement.announcementType
            };

            this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.AnnouncementImpression, params);

            this.initializeAnnouncementButton(this.announcement.primaryButton);
            this.initializeAnnouncementButton(this.announcement.secondaryButton);
            this.initializeAnnouncementButton(this.announcement.borderlessButton);

            if (this.announcement.dismissObservable) {
                this.announcement.dismissObservable.pipe(
                    filter(x => x),
                    take(1),
                    takeUntil(this.ngUnsubscribe)
                ).subscribe(() => {
                    this.dismiss();
                });
            }

            if (this.announcement.dismissTimeoutInSecond) {
                setTimeout(
                    this.dismiss.bind(this),
                    this.announcement.dismissTimeoutInSecond * 1000
                );
            }
        }
    }

    public ngAfterViewInit() {
        if (this.announcement.titleRedirectionSuggestion && this.announcement.titleRedirectionSuggestion.shouldFocusOnInit) {
            const titleRedirectionElement = document.getElementById(`announcement-suggestion-link_${this.announcement.index}`);
            if (titleRedirectionElement) {
                titleRedirectionElement.focus();
            }
        }

        const el = document.createElement('span');
        const id = `announcement-aria-${this.announcement.index}`;
        el.setAttribute('id', id);
        el.setAttribute('aria-live', 'polite');
        el.classList.add('visually-hidden');
        document.body.appendChild(el);

        setTimeout(() => {
            document.getElementById(id).innerHTML = this.announcement.title;
        }, 100);

        setTimeout(() => {
            document.body.removeChild(document.getElementById(id));
        }, 1000);
    }

    public ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public onDismissClick() {
        this.dismiss();
    }

    private initializeAnnouncementButton(button: AnnouncementButtonModel): void {
        if (!button || (!button.onClick && !button.url && !button.dismissOnClick)) {
            return;
        }

        if (!button.onClick && button.url) {
            // use default behavior
            button.onClick = () => {
                this.urlLauncherService.openUrl(button.url, LaunchAction.AnnouncementAction);
            };
        }

        if (button.dismissOnClick) {
            const onClick = button.onClick;

            button.onClick = () => {
                if (onClick) {
                    onClick();
                }
                this.dismiss();
            };
        }
    }

    private dismiss() {
        this.showAnnouncement = false;
        this.eventService.broadcastEvent({
            name: AngularEvents.closeAnnouncement,
            args: [this.announcement.announcementType, this.announcement.index]
        });

        if (this.announcement.dismissCookieName) {
            const cookieOptions: CookieOptions = {
                secure: true
            };

            this.cookieService.put(this.announcement.dismissCookieName, '1', cookieOptions);
        }
    }
}
